import { FormInstance } from 'antd/lib/form'
import { ActionParam } from 'Core/ActionCore/ActionManager'
import { SubmitModalController } from 'Core/ModalCore/Modals/SubmitModalController'
import { Studio } from 'Core/Studio'
import { ITask } from 'Core/TaskAdapter'
import { MetaSubmitGenerateResponseDataPayload } from 'HTTPClients/customTask'
import { debounce } from 'lodash'
import { submitGenerateResponseData, submitTurnData } from 'Services/customTask.service'
import { CommonTask } from 'Tasks/CommonTask/CommonTask'
import { CustomTaskUi } from 'Tasks/CustomTask/UI/CustomTaskUI'
import { CustomForm, PromptExtraInfo } from 'Types/CustomTask.types'
import {
    AttributeDefinitionType,
    ContainerDataType,
    ContainerType,
    CustomDataType,
    CustomSubmitTurnDataPayload,
    GenerateResponseFormType,
    HumanResponseFormType,
    ManualResponseFormType,
    NotificationPropsType,
    PrimaryResponseFormType,
    PromptGenerationFormType,
    PromptGenerationFromContextFormType,
    StepTypes,
    TaskAdditionalInfo,
    TaskConfigType,
    TurnData,
    TurnDataType,
    TurnResponseFormType,
} from 'Types/Tasks.types'
import { createReactElement } from 'shared/utils/react.utils'
import { CustomContainerStrategy } from 'Core/StateContainerCore/ContainerStrategies/CustomContainerStrategy'
import { ViolationsModalController } from 'Core/ModalCore/Modals/ViolationsModalController'
import type { CSATFeedbackData } from 'Types/CSAT.types'
import {
    buildForcedPlayTurnGeneratePayload,
    fillProctoringTimeoutExtraInfo,
    PROCTORING_TIMEOUT_TEXT,
} from 'Utils/proctoringSubmit.utils'

export class CustomTask
    extends CommonTask<ContainerType.Custom, CustomDataType>
    implements ITask<TaskConfigType<ContainerType.Custom, CustomDataType>>
{
    private isLoading?: boolean
    private turnData?: {
        data: TurnData
        turnNumber: number
        retryCount: number
    }
    private finalSubmissionForm?: FormInstance
    private generateResponseForm?: FormInstance<GenerateResponseFormType>
    private primaryResponseForms?: FormInstance<PrimaryResponseFormType>[]
    private turnResponseForms?: FormInstance<TurnResponseFormType>[]
    private humanResponseForm?: FormInstance<HumanResponseFormType>
    private promptGenerationForm?: FormInstance<PromptGenerationFormType>
    private promptGenerationFromContextForm?: FormInstance<PromptGenerationFromContextFormType>
    private manualResponseForm?: FormInstance<ManualResponseFormType>
    private prompt: string = ''
    private promptExtraInfo: PromptExtraInfo = {
        promptOptions: [],
        promptContext: '',
        promptContextDocumentUrl: '',
        promptContextDocumentName: '',
        promptContextDocumentPageNumber: '',
        externalModelResponse: '',
        externalModelName: '',
    }

    constructor(protected studio: Studio) {
        const customContainerStrategy = new CustomContainerStrategy()
        super(studio, customContainerStrategy)
        customContainerStrategy.setContext({
            task: this,
        })
    }

    initialize(config: TaskConfigType<ContainerType.Custom, CustomDataType>): void {
        this.initializePrompt()
        this.initializePromptExtraInfo()

        const taskAdditionalInfo: TaskAdditionalInfo = {
            taskId: config.assignment.task.id,
            projectId: config.assignment.project_id,
            assignedUserId: config.assignment.user_id,
            stepId: config.assignment.step?.id,
            useCase: config.assignment.task.type,
            version: config.assignment.task.input_container.data.data.main_info?.value.version,
        }

        this.studio.workbench.enqueueUiUpdate(workbenchUi => {
            this.attributeManager?.useAttributeGroup({
                type: AttributeDefinitionType.EXTRA_INFO,
                groupKey: 'extra-info',
                attributes: config.assignment.task.attributes.filter(
                    attr => attr.type === AttributeDefinitionType.EXTRA_INFO
                ),
                config: { useInCollection: false, props: { header: '' } },
            })
            const attributesUI = this.attributeManager
                ?.getAttributeGroupsByDefinitionType(AttributeDefinitionType.EXTRA_INFO)[0]
                ?.getElement()
            this.addPluginsMandatoryViolation()
            this.ui = createReactElement(CustomTaskUi, {
                getOutputDetails: () => config.assignment.task.output_start_state_container,
                keyBindingActions: this.keyBindingActions,
                taskAdditionalInfo: taskAdditionalInfo,
                taskMode: config.mode,
                getPrompt: (): string => this.prompt,
                getPromptInstructions: (): string => this.getPromptInstructions(),
                getPromptExtraInfo: () => this.promptExtraInfo,
                featureFlags: this.studio.studioConfig.featureFlags,
                isPromptGenerationAllowed: () => this.isPromptGenerationAllowed(),
                isLoading: () => this.isLoading ?? false,
                turnData: () => this.turnData,
                onSubmitGenerateResponseForm: (turnDataType: TurnDataType) => {
                    this.submitGenerateResponseForm(turnDataType)
                },
                onRetryTurn: () => {
                    this.onRetryTurn()
                },
                onPromptRevisionCreation: async (type: 'PROMPT_REVISION' | 'RETRY_NON_TURN') => {
                    return await this.onPromptRevisionCreation(type)
                },
                onContainerUpdate: this.updateDraftContainerAfterPromptGeneration,
                onSubmitTurnData: (
                    type: 'TURN_ANNOTATION' | 'CORRECT_ANSWER',
                    firstIncorrectStep?: number | null
                ) => {
                    this.onSubmitTurnData(type, firstIncorrectStep)
                },
                isReadOnly: () => this.isReadOnly,
                setForms: (forms: CustomForm) => {
                    if (forms.generateResponseForm)
                        this.generateResponseForm = forms.generateResponseForm
                    if (forms.promptGenerationForm)
                        this.promptGenerationForm = forms.promptGenerationForm
                    if (forms.primaryResponseForms)
                        this.primaryResponseForms = forms.primaryResponseForms
                    if (forms.turnResponseForms) this.turnResponseForms = forms.turnResponseForms
                    if (forms.humanResponseForm) this.humanResponseForm = forms.humanResponseForm
                    if (forms.promptGenerationFromContextForm)
                        this.promptGenerationFromContextForm = forms.promptGenerationFromContextForm
                    if (forms.manualResponseForm) this.manualResponseForm = forms.manualResponseForm
                    if (forms.finalSubmissionForm)
                        this.finalSubmissionForm = forms.finalSubmissionForm
                },
                installedPlugins: this.studio.pluginManager.getInstalledPlugins(),
                attributesUI,
                getQuestion: () => {
                    return (
                        this.promptGenerationForm?.getFieldValue('prompt') ??
                        this.promptGenerationFromContextForm?.getFieldValue('prompt') ??
                        ''
                    )
                },
                setIsLiveFeedbackChecked: () => this.removeMandatoryViolation('live-feedback'),
                liveFeedbackContext: {
                    userId: config.assignment.user_id,
                    jobId: config.assignment.task.job_id,
                    refId: config.assignment.task.id,
                    inputContainer: config.assignment.task.input_container,
                    getContainerData: () => {
                        try {
                            return this.taskState.getModifiedDraftOutputContainer(
                                this.initialConfig
                            )
                        } catch (error) {
                            console.error('Error getting modified container:', error)
                            return this.taskState.getDraftOutputContainer()
                        }
                    },
                },
                areAttributesValid: this.areLiveFeedbackRequirementsValid,
            })

            this.addCommonWorkbenchComponents(workbenchUi, config)

            if (
                config.assignment.task.attributes?.filter(
                    item =>
                        item.type === AttributeDefinitionType.QUALITY_EVALUATION &&
                        item.is_value_editable
                ).length > 0
            ) {
                this.attributeManager?.useAttributeGroup({
                    type: AttributeDefinitionType.QUALITY_EVALUATION,
                    groupKey: 'quality-evaluation',
                    attributes: config.assignment.task.attributes.filter(
                        attr => attr.type === AttributeDefinitionType.QUALITY_EVALUATION
                    ),
                    config: { useInCollection: true },
                })

                workbenchUi.rightSidebar.addDivider()
            }
        })
    }

    readonly submitTask = async (options?: {
        onComplete?: (props?: NotificationPropsType) => void
        feedbackData?: CSATFeedbackData
        useDummyIfEmpty?: boolean
    }) => {
        // Timer-end forced submit: use submission-play-turn with dummy required fields.
        if (options?.useDummyIfEmpty) {
            return this.submitTaskWithDummyPlayTurn(options)
        }

        const step = this.studio.taskConfig.assignment.step
        if (step?.type === StepTypes.Maker || step?.type === StepTypes.Rework) {
            return this.submitMakerOrReworkTask(options)
        }

        super.submitTask({
            onComplete: (notifications?: NotificationPropsType) => {
                this.studio.nextTask(notifications)
            },
            feedbackData: options?.feedbackData,
        })
    }

    private readonly submitMakerOrReworkTask = async (options?: {
        onComplete?: (props?: NotificationPropsType) => void
        feedbackData?: CSATFeedbackData
    }) => {
        try {
            this.isLoading = true
            this.ui?.updater.forceUpdate()
            const isLMFProject = this.studio.studioConfig.featureFlags?.lmf_project
            const isSTEMProject = this.studio.studioConfig.featureFlags?.stem_project
            const isOTSProject = this.studio.studioConfig.featureFlags?.ots_project
            const isTurnAnnotationEnabled =
                this.studio.studioConfig.featureFlags?.turn_annotation_enabled

            if (!isLMFProject && isSTEMProject && isTurnAnnotationEnabled) return

            const turnDataType =
                isLMFProject || isSTEMProject || !isTurnAnnotationEnabled
                    ? 'PROMPT_FINALIZATION'
                    : ''

            if (!turnDataType) throw new Error('Turn data type is not defined')

            const extraInfo =
                this.taskState.getDraftOutputContainer()?.data.data.response?.extra_info

            const reader =
                (await submitGenerateResponseData(
                    this.taskState.getCurrentState()?.user_id ?? '',
                    this.taskState.getCurrentState()?.submission.id ?? '',
                    Math.floor(this.studio.taskManager.timer.getElapsedTime() / 1000),
                    {
                        ...(isLMFProject || isOTSProject
                            ? this.manualResponseForm?.getFieldsValue?.() ?? {}
                            : this.generateResponseForm?.getFieldsValue?.() ?? {}),
                        turnDataType,
                    },
                    extraInfo,
                    this.taskState.getDraftOutputContainer()?.supplement
                )) ?? null

            await this.streamResponseAndUpdateTurnData(reader, () => {
                if (turnDataType !== 'PROMPT_FINALIZATION') return

                this.prompt = this.promptGenerationForm?.getFieldValue('prompt') ?? ''

                if (this.studio.studioConfig.featureFlags?.stem_project) {
                    this.promptExtraInfo.promptOptions =
                        this.promptGenerationForm?.getFieldValue('options') ?? []
                }

                if (
                    !this.studio.studioConfig.featureFlags?.turn_annotation_enabled ||
                    isLMFProject
                ) {
                    options?.onComplete?.({
                        type: 'success',
                        message: 'Task submitted successfully',
                    })
                }
            })
        } catch (e) {
            this.studio.workbench.enqueueUiUpdate(ui =>
                ui.notifier.notification.error({ message: (e as Error).message })
            )
            if ((e as Error).message === 'Task submission cancelled by admin.') {
                options?.onComplete?.({ type: 'error', message: (e as Error).message })
            }
        } finally {
            this.isLoading = false
            this.ui?.updater.forceUpdate()
        }
    }

    private readonly submitTaskWithDummyPlayTurn = async (options?: {
        onComplete?: (props?: NotificationPropsType) => void
        feedbackData?: CSATFeedbackData
        useDummyIfEmpty?: boolean
    }) => {
        const step = this.studio.taskConfig.assignment.step
        if (!step?.type || ![StepTypes.Maker, StepTypes.Rework].includes(step.type)) {
            return super.submitTask(options)
        }

        try {
            this.isLoading = true
            this.ui?.updater.forceUpdate()

            const taskState = this.taskState.getCurrentState()
            if (!taskState) {
                options?.onComplete?.({
                    type: 'error',
                    message: 'Cannot submit task: task state is not initialized',
                })
                return
            }

            const userId = taskState.user_id
            const submissionId = taskState.submission.id
            const timeElapsedInSeconds = Math.ceil(
                this.studio.taskManager.timer.getElapsedTime() / 1000
            )

            const draft = this.taskState.getDraftOutputContainer()
            const existingPrompt =
                draft?.data?.data?.response?.main_info?.value?.prompt ||
                this.promptGenerationForm?.getFieldValue('prompt') ||
                this.prompt ||
                ''

            // Draft with no prompt: create one first so PROMPT_FINALIZATION can succeed.
            if (!String(existingPrompt).trim()) {
                const revisionReader =
                    (await submitTurnData(userId, submissionId, timeElapsedInSeconds, {
                        type: 'PROMPT_REVISION',
                        data: { prompt: PROCTORING_TIMEOUT_TEXT },
                    })) ?? null
                await this.streamResponseAndUpdateTurnData(revisionReader)
            }

            const isLMFProject = this.studio.studioConfig.featureFlags?.lmf_project
            const isOTSProject = this.studio.studioConfig.featureFlags?.ots_project
            const formValues =
                isLMFProject || isOTSProject
                    ? this.manualResponseForm?.getFieldsValue?.() ?? {}
                    : this.generateResponseForm?.getFieldsValue?.() ?? {}

            // Prefer user form/draft values; dummy only fills empty required fields.
            const { payload, usedDummy } = buildForcedPlayTurnGeneratePayload(formValues, draft)

            const draftExtraInfo = draft?.data?.data?.response?.extra_info
            const extraInfo = fillProctoringTimeoutExtraInfo(
                this.initialConfig.assignment?.task?.attributes,
                draftExtraInfo
            )

            const reader =
                (await submitGenerateResponseData(
                    userId,
                    submissionId,
                    timeElapsedInSeconds,
                    payload,
                    extraInfo,
                    draft?.supplement
                )) ?? null

            await this.streamResponseAndUpdateTurnData(reader, () => {
                options?.onComplete?.({
                    type: 'success',
                    message: usedDummy
                        ? 'Time ended. Task was submitted automatically.'
                        : 'Task submitted successfully',
                })
            })
        } catch (e) {
            console.warn(
                '[proctoring] custom-task duration-end play-turn failed; falling back to draft submit',
                {
                    stepType: step?.type,
                    submissionId: this.taskState.getCurrentState()?.submission.id,
                    error: e,
                }
            )
            try {
                await super.submitTask(options)
            } catch (fallbackError) {
                this.studio.workbench.enqueueUiUpdate(ui =>
                    ui.notifier.notification.error({
                        message: (fallbackError as Error).message,
                    })
                )
                options?.onComplete?.({
                    type: 'error',
                    message: (fallbackError as Error).message,
                })
            }
        } finally {
            this.isLoading = false
            this.ui?.updater.forceUpdate()
        }
    }

    private addPluginsMandatoryViolation() {
        const installedPlugins = this.studio.pluginManager.getInstalledPlugins()

        installedPlugins.forEach(plugin => {
            if (plugin.id === 'Live Feedback') {
                this.addMandatoryViolation(
                    'live-feedback',
                    'You need to run Live Feedback before submitting'
                )
            }
        })
    }

    private addMandatoryViolation(id: string, message: string) {
        this.studio?.inputManager.addViolation({
            id: `mandatory_plugin_${id}`,
            message,
            type: 'error',
        })
    }

    private removeMandatoryViolation(id: string) {
        this.studio?.inputManager.removeViolation(`mandatory_plugin_${id}`, 'error')
    }

    private isPromptGenerationAllowed = (): boolean => {
        const version =
            this.initialConfig!.assignment?.task?.input_container?.data?.data?.main_info?.value
                .version

        /*
        Prompt generation should be allowed only in maker and rework steps for version 1 job
        till we set the turn data (PROMPT_FINALIZATION)
        */

        return (
            version === 1 &&
            !this.turnData &&
            (!this.initialConfig!.assignment.output_history?.length ||
                [StepTypes.Maker, StepTypes.Rework].includes(
                    this.initialConfig!.assignment.step!.type
                ))
        )
    }

    initializePrompt(): void {
        const version =
            this.initialConfig!.assignment?.task?.input_container?.data?.data?.main_info?.value
                .version

        // prompt is stored as input in older version where prompts are already created
        if (version !== 1) {
            this.prompt =
                this.initialConfig!.assignment?.task?.input_container?.data?.data?.main_info?.value
                    .prompt ?? ''
            return
        }

        // after first step we will pick the prompt from the last available step for version 1
        if (this.initialConfig!.assignment.output_history?.length) {
            const index = this.initialConfig!.assignment.output_history?.length
                ? this.initialConfig!.assignment.output_history?.length - 1
                : 0
            this.prompt =
                (
                    this.initialConfig?.assignment.output_history?.[index].output.data
                        .data as CustomDataType
                ).response.main_info?.value.prompt ?? ''
            return
        }
    }

    initializePromptExtraInfo(): void {
        if (!this.studio.studioConfig.featureFlags?.stem_project) {
            this.promptExtraInfo.promptOptions = undefined
        }

        if (!this.studio.studioConfig.featureFlags?.lmf_project) {
            this.promptExtraInfo.promptContext = undefined
            this.promptExtraInfo.promptContextDocumentUrl = undefined
            this.promptExtraInfo.promptContextDocumentName = undefined
            this.promptExtraInfo.promptContextDocumentPageNumber = undefined
        }

        if (this.initialConfig!.assignment.output_history?.length) {
            const index = this.initialConfig!.assignment.output_history?.length
                ? this.initialConfig!.assignment.output_history?.length - 1
                : 0

            const responseMainInfo = (
                this.initialConfig?.assignment.output_history?.[index].output.data
                    .data as CustomDataType
            ).response.main_info?.value

            this.promptExtraInfo = {
                promptOptions: responseMainInfo.options ?? [],
                promptContext: responseMainInfo.context,
                promptContextDocumentUrl: responseMainInfo.context_document_url,
                promptContextDocumentName: responseMainInfo.context_document_name,
                promptContextDocumentPageNumber: responseMainInfo.context_document_page_numbers,
                externalModelName: responseMainInfo.external_model_name,
                externalModelResponse: responseMainInfo.external_model_response,
            }
        }
    }

    getPromptInstructions(): string {
        return (
            this.initialConfig!.assignment?.task?.input_container?.data?.data?.main_info?.value
                .prompt_instruction ?? ''
        )
    }

    private readonly areLiveFeedbackRequirementsValid = async (): Promise<boolean> => {
        try {
            const attributesValid = (await this.attributeManager?.validateGroups()) ?? true
            return attributesValid
        } catch (error) {
            console.error('Live feedback validation error:', error)
            return false
        }
    }

    validateData = async () => {
        return (
            (await this.attributeManager?.validateGroups()) &&
            (await this.validateForms()) &&
            this.finalSubmissionForm?.validateFields().catch(() => false)
        )
    }

    validateForms = async (): Promise<boolean> => {
        const validateForm = async (form?: FormInstance): Promise<boolean> => {
            if (!form) return true
            try {
                await form.validateFields()
                return true
            } catch {
                return false
            }
        }

        const validateFormArray = async (forms?: FormInstance[]): Promise<boolean> => {
            if (!forms) return true
            const results = await Promise.all(forms.map(validateForm))
            return results.every(Boolean)
        }

        return (
            (await validateForm(this.promptGenerationForm)) &&
            (await validateForm(this.promptGenerationFromContextForm)) &&
            (await validateForm(this.generateResponseForm)) &&
            (await validateFormArray(this.primaryResponseForms)) &&
            (await validateForm(this.humanResponseForm)) &&
            (await validateFormArray(this.turnResponseForms)) &&
            (await validateForm(this.manualResponseForm))
        )
    }

    addMakerActions() {
        const makerActions: ActionParam[] = []

        if (!this.isSavedTask) {
            makerActions.push({
                id: 'Submit',
                config: {
                    onClick: async () => {
                        const config = this.studio.taskConfig as TaskConfigType<
                            ContainerType.Custom,
                            CustomDataType
                        >
                        const canSubmit = await this.validateData()
                        if (!canSubmit) {
                            this.studio.workbench.enqueueUiUpdate(ui =>
                                ui.notifier.notification.error({
                                    message: "You haven't filled all the required details",
                                })
                            )
                            return
                        }
                        const errors = this.studio.inputManager.getViolationCount('error')
                        if (errors > 0) {
                            this.modalManager?.showModal(ViolationsModalController.Id)
                            this.ui?.updater.forceUpdate()
                            return
                        }
                        this.modalManager?.setModalMetaData(SubmitModalController.Id, {
                            taskMode: config.mode,
                        })
                        this.modalManager?.showModal(SubmitModalController.Id)
                        this.ui?.updater.forceUpdate()
                    },
                },
            })
        } else {
            makerActions.push({ id: 'SubmitAndExit' })
        }

        const step = this.studio.taskConfig.assignment.step
        if (step?.type === StepTypes.Maker || step?.type === StepTypes.Rework || !this.isReadOnly) {
            makerActions.push({
                id: 'Exit',
                config: {
                    onClick: async () => this.studio.exitTask(),
                },
            })
        } else {
            makerActions.push({ id: 'Exit' })
        }

        if (this.initialConfig?.assignment.task.config.skip_task_config?.is_enabled) {
            makerActions.push({ id: 'Skip' })
        }

        makerActions.push({ id: 'SaveForLater' })

        this.actionManager?.addActions(makerActions)
    }

    protected autoSaveTask = debounce(async () => {})

    protected keyBindingActions = (_: KeyboardEvent) => {}

    private streamResponseAndUpdateTurnData = async (
        reader: ReadableStreamDefaultReader<Uint8Array> | null,
        onPartial?: (partial: MetaSubmitGenerateResponseDataPayload) => void,
        onComplete?: () => void
    ) => {
        if (!reader) {
            throw new Error('Something went wrong!')
        }

        const decoder = new TextDecoder()
        let done = false
        let buffer = ''

        while (!done) {
            const { value, done: streamDone } = await reader.read()
            done = streamDone
            const chunk = decoder.decode(value || new Uint8Array(), { stream: true })
            buffer += chunk

            if (!done) {
                try {
                    const partial = JSON.parse(buffer) as MetaSubmitGenerateResponseDataPayload

                    // Common turnData update
                    const turnDataList =
                        partial.submission.output_container.data.data.response.main_info.value.turns
                    if (turnDataList.length) {
                        this.turnData = {
                            data: turnDataList[turnDataList.length - 1],
                            turnNumber: turnDataList.length,
                            retryCount: turnDataList[turnDataList.length - 1].retry_count ?? 0,
                        }
                    }

                    onPartial?.(partial)
                    this.ui?.updater.forceUpdate()
                    buffer = ''
                } catch {
                    // Ignore parse errors
                }
            } else {
                if (buffer) {
                    console.error('Failed to parse final JSON')
                    throw new Error('Invalid model response format.')
                } else {
                    onComplete?.()
                }
            }
        }
    }

    private submitGenerateResponseForm = async (
        turnDataType: TurnDataType = 'PROMPT_ANNOTATION'
    ) => {
        try {
            this.isLoading = true
            this.ui?.updater.forceUpdate()
            const reader =
                (await submitGenerateResponseData(
                    this.taskState.getCurrentState()?.user_id ?? '',
                    this.taskState.getCurrentState()?.submission.id ?? '',
                    Math.floor(this.studio.taskManager.timer.getElapsedTime() / 1000),
                    {
                        ...this.generateResponseForm!.getFieldsValue(),
                        turnDataType,
                    }
                )) ?? null

            await this.streamResponseAndUpdateTurnData(reader, () => {
                this.prompt = this.promptGenerationForm?.getFieldValue('prompt') ?? ''
            })
        } catch (e) {
            this.studio.workbench.enqueueUiUpdate(ui =>
                ui.notifier.notification.error({ message: (e as Error).message })
            )
        } finally {
            this.isLoading = false
            this.ui?.updater.forceUpdate()
        }
    }

    private onRetryTurn = async () => {
        try {
            this.isLoading = true
            this.ui?.updater.forceUpdate()

            const reader =
                (await submitTurnData(
                    this.taskState.getCurrentState()?.user_id ?? '',
                    this.taskState.getCurrentState()?.submission.id ?? '',
                    Math.floor(this.studio.taskManager.timer.getElapsedTime() / 1000),
                    {
                        type: 'RETRY_TURN',
                        data: {},
                    }
                )) ?? null
            await this.streamResponseAndUpdateTurnData(reader)
        } catch (e) {
            this.studio.workbench.enqueueUiUpdate(ui =>
                ui.notifier.notification.error({ message: (e as Error).message })
            )
        } finally {
            this.isLoading = false
            this.ui?.updater.forceUpdate()
        }
    }

    private updateDraftContainerAfterPromptGeneration = (
        container: ContainerDataType<ContainerType.Custom, CustomDataType>
    ) => {
        try {
            this.taskState.updateDraftOutputContainer(container)
        } catch (error) {
            console.error('Failed to update draft container:', error)
        }
    }

    private onPromptRevisionCreation = async (
        type: 'PROMPT_REVISION' | 'RETRY_NON_TURN'
    ): Promise<ReadableStreamDefaultReader<Uint8Array<ArrayBufferLike>> | undefined> => {
        try {
            const promptGenerationFormValues = this.promptGenerationForm?.getFieldsValue()
            const promptGenerationFromContextFormValues =
                this.promptGenerationFromContextForm?.getFieldsValue()

            let data = {}

            if (this.studio.studioConfig.featureFlags?.lmf_project) {
                const contextDocumentPageNumber =
                    promptGenerationFromContextFormValues?.contextDocumentPageNumber
                        ?.map(record => record.pageNumber.trim())
                        .join(',')
                data = {
                    prompt: promptGenerationFromContextFormValues?.prompt,
                    context: promptGenerationFromContextFormValues?.context,
                    context_document_url: promptGenerationFromContextFormValues?.contextDocumentUrl,
                    context_document_name:
                        promptGenerationFromContextFormValues?.contextDocumentName,
                    context_document_page_numbers: contextDocumentPageNumber,
                }
            } else {
                data = {
                    prompt: promptGenerationFormValues?.prompt,
                    context: promptGenerationFormValues?.context,
                    context_document_url: promptGenerationFormValues?.contextDocumentUrl,
                    external_model_name: promptGenerationFormValues?.externalModelName,
                    external_model_response: promptGenerationFormValues?.externalModelResponse,
                    ...(this.studio.studioConfig.featureFlags?.stem_project
                        ? { options: promptGenerationFormValues?.options }
                        : {}),
                }
            }
            const reader = await submitTurnData(
                this.taskState.getCurrentState()?.user_id ?? '',
                this.taskState.getCurrentState()?.submission.id ?? '',
                Math.floor(this.studio.taskManager.timer.getElapsedTime() / 1000),
                {
                    type,
                    data: type === 'RETRY_NON_TURN' ? {} : data,
                }
            )
            return reader
        } catch (e) {
            this.studio.workbench.enqueueUiUpdate(ui =>
                ui.notifier.notification.error({ message: 'Failed to validate prompt' })
            )
        } finally {
            this.isLoading = false
            this.ui?.updater.forceUpdate()
        }
    }

    private getDataForTurnAnnotation(firstIncorrectStep: number | null) {
        const steps = this.turnData?.data.steps ?? []

        const data: CustomSubmitTurnDataPayload = {
            type: 'TURN_ANNOTATION',
            data: {
                turn_number: this.turnData?.turnNumber ?? 0,
                response_annotation: {
                    accuracy: this.primaryResponseForms?.[0].getFieldValue('isCorrect')
                        ? 'YES'
                        : 'NO',
                    instruction_following: this.primaryResponseForms?.[0].getFieldValue(
                        'followInstructions'
                    )
                        ? 'YES'
                        : 'NO',
                    skill_check: this.primaryResponseForms?.[0]
                        .getFieldValue('skillsUsed')
                        .toUpperCase(),
                    first_incorrect_step:
                        firstIncorrectStep === null ? null : [...steps][firstIncorrectStep].number,
                    skill_gaps: this.primaryResponseForms?.[0].getFieldValue('skillsGap'),
                },
                step_annotations: null,
                human_final_answer: this.turnResponseForms?.[0].getFieldValue('human_final_answer'),
            },
        }

        if (this.primaryResponseForms?.[0].getFieldValue('isCorrect')) {
            data.data.step_annotations = []

            for (let i = 0; i < steps.length; i++) {
                data.data.step_annotations.push({
                    step_number: this.turnResponseForms?.[0].getFieldValue('values')[i].step_number,
                    accuracy: this.turnResponseForms?.[0]
                        .getFieldValue('values')
                        // eslint-disable-next-line no-unexpected-multiline
                        [i].accuracy.toUpperCase(),
                    python_or_text: this.turnResponseForms?.[0]
                        .getFieldValue('values')
                        // eslint-disable-next-line no-unexpected-multiline
                        [i].python_or_text.toUpperCase(),
                    error_identification: null,
                    error_correction: null,
                })
            }
        } else if (firstIncorrectStep !== null) {
            data.data.step_annotations = []

            for (let i = 0; i <= firstIncorrectStep; i++) {
                data.data.step_annotations.push({
                    step_number: this.turnResponseForms?.[0].getFieldValue('values')[i].step_number,
                    accuracy: this.turnResponseForms?.[0]
                        .getFieldValue('values')
                        // eslint-disable-next-line no-unexpected-multiline
                        [i].accuracy.toUpperCase(),
                    python_or_text: this.turnResponseForms?.[0]
                        .getFieldValue('values')
                        // eslint-disable-next-line no-unexpected-multiline
                        [i].python_or_text.toUpperCase(),
                    error_identification:
                        this.turnResponseForms?.[0].getFieldValue('values')[i].error_identification,
                    error_correction:
                        this.turnResponseForms?.[0].getFieldValue('values')[i].error_correction,
                })
            }
        }

        return data
    }

    private getDataForCorrectAnswer() {
        const data = {
            type: 'CORRECT_ANSWER',
            data: {
                steps: this.humanResponseForm?.getFieldValue('values').map(
                    (
                        step: {
                            is_correct: boolean
                            python_or_llm: string
                            correct_step: string
                            error_identification: string | null
                            error_correction: string | null
                        },
                        index: number
                    ) => {
                        return {
                            accuracy: step.is_correct ? 'YES' : 'NO',
                            step_number: index + 1,
                            content: step.correct_step,
                            error_identification: step.is_correct
                                ? null
                                : step.error_identification,
                            error_correction: step.is_correct ? null : step.error_correction,
                        }
                    }
                ),
                human_final_answer: this.humanResponseForm?.getFieldValue('human_final_answer'),
            },
        }

        return data
    }

    private onSubmitTurnData = async (
        type: 'TURN_ANNOTATION' | 'CORRECT_ANSWER',
        firstIncorrectStep?: number | null
    ) => {
        try {
            this.isLoading = true
            this.ui?.updater.forceUpdate()

            const data =
                type === 'TURN_ANNOTATION'
                    ? this.getDataForTurnAnnotation(firstIncorrectStep!)
                    : this.getDataForCorrectAnswer()

            const reader =
                (await submitTurnData(
                    this.taskState.getCurrentState()?.user_id ?? '',
                    this.taskState.getCurrentState()?.submission.id ?? '',
                    Math.floor(this.studio.taskManager.timer.getElapsedTime() / 1000),
                    data
                )) ?? null
            await this.streamResponseAndUpdateTurnData(reader, partial => {
                if (partial.submission.output_container.data.data.response.is_complete) {
                    this.studio.nextTask({
                        type: 'success',
                        message: 'Task submitted successfully',
                    })
                }
            })
        } catch (e) {
            this.studio.workbench.enqueueUiUpdate(ui =>
                ui.notifier.notification.error({ message: (e as Error).message })
            )
        } finally {
            this.isLoading = false
            this.ui?.updater.forceUpdate()
        }
    }

    getGenerateResponseForm() {
        return this.generateResponseForm
    }

    getManualResponseForm() {
        return this.manualResponseForm
    }

    getHumanResponseForm() {
        return this.humanResponseForm
    }

    getTurnResponseForms() {
        return this.turnResponseForms
    }

    getPrimaryResponseForms() {
        return this.primaryResponseForms
    }
}
